// Auto Helper: um "bot" simples para a caça automática. Tudo que ele usa fica na barra de ações (a ordem da barra
// é a prioridade). As abas mostram só o que importa em cada parte: curas, ataque, suporte, condições, monstros
// especiais e loot, com a explicação de cada opção e de cada magia.
// Visual de "central de comando" (aço, verde-terminal e âmbar). CSS em client/css-parts/helper.css (prefixo .ah2-).
import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { BUFFS } from '../../../../shared/src/game/buffs';
import { CHALLENGE_SPELLS, CONDITION_CURES, DEBUFF_SPELLS, DEFAULT_MOVEMENT, FAMILIARS, HEAL_SPELLS, PARTY_BUFFS, slot as mkSlot, type HelperSettings, type HotbarSlot } from '../../../../shared/src/game/helper';
import { POTIONS, potionBlock, potionOf } from '../../../../shared/src/game/combat';
import { AREA_NAMES, ELEMENT_NAMES, spellBlock, type SpellDef } from '../../../../shared/src/game/spells';
import type { VocationId } from '../../../../shared/src/game/vocations';
import type { ItemInstance } from '../../../../shared/src/game/items';
import { catalogNow } from '../items';
import { potionPriceByName } from '../potionPrice';
import { runeByItem, spellByWords, spellsNow } from '../spells';
import { ItemImg } from './ItemSlot';
import { SpellIcon } from './SpellIcon';
import { slotLabel } from './ActionBar';

type Kind = 'heal' | 'attack' | 'buff' | 'other';
type Tab = 'geral' | 'heal' | 'attack' | 'buff' | 'cond' | 'elite' | 'loot';
const TABS: Array<[Tab, string, string, string]> = [
  ['geral', 'Geral', '⚙️', 'Opções, PvP e movimentação'], ['heal', 'Curas', '❤️', 'Magias, poções e runas de cura'], ['attack', 'Ataque', '⚔️', 'Magias e runas de ataque (a barra de magias)'],
  ['buff', 'Suporte', '✨', 'Haste, magic shield e outros efeitos'], ['cond', 'Condições', '☠️', 'Curar veneno, fogo, paralisia...'], ['elite', 'Especiais', '⭐', 'Monstros Super, Mega e Ultra'], ['loot', 'Loot', '🎒', 'O que não pegar e o que não vender'],
];
const MV_NAME: Record<string, string> = { natural: 'Natural', lure: 'Lurar (box)', distance: 'Distância' };
const gp = (v: number) => v.toLocaleString('pt-BR');

function kindOf(sl: HotbarSlot): Kind {
  if ('spell' in sl.use) {
    const w = sl.use.spell;
    if (BUFFS[w] || PARTY_BUFFS[w] || FAMILIARS[w] || CHALLENGE_SPELLS.includes(w)) return 'buff';
    if (DEBUFF_SPELLS[w]) return 'attack';
    if (HEAL_SPELLS.some((h) => h.words === w)) return 'heal';
    return spellByWords(w)?.group === 'attack' ? 'attack' : 'other';
  }
  const name = catalogNow()?.get(sl.use.item)?.name ?? '';
  if (name in POTIONS) return 'heal';
  const r = runeByItem(sl.use.item);
  return r?.group === 'healing' ? 'heal' : r?.group === 'attack' ? 'attack' : 'other';
}

const secs = (ms: number) => (ms >= 60000 ? `${Math.round(ms / 60000)} min` : `${Math.round(ms / 1000)} s`);
export function spellInfo(s: SpellDef | undefined): string {
  if (!s) return '';
  const parts = [s.kind === 'rune' ? `runa (ML ${s.ml})` : `${s.mana} de mana`, `espera ${secs(s.cooldown || 2000)}`];
  if (s.element && ELEMENT_NAMES[s.element]) parts.push(ELEMENT_NAMES[s.element]!);
  if (s.group === 'attack') parts.push(AREA_NAMES[s.area.type]);
  if (s.monk) parts.push(s.monk === 'builder' ? 'builder: gera 1 de Harmony' : 'spender: gasta a Harmony e bate mais forte');
  return parts.join(' · ');
}

interface Opt { key: string; name: string; words?: string; spell?: SpellDef; item?: number; req: string; what: string; lock: string | null; sort: number }

/** Explica em palavras simples o que a magia/runa faz. */
export function whatSpell(s: SpellDef): string {
  const el = s.element && ELEMENT_NAMES[s.element] ? ELEMENT_NAMES[s.element] : '';
  const area: Record<string, string> = {
    single: 'em um monstro (o alvo)', self: 'em você', circle: 'em todos os monstros em volta de você', 'target-circle': 'no alvo e nos monstros em volta dele',
    wave: 'em leque à sua frente', beam: 'em linha reta à sua frente', chain: 'pulando de monstro em monstro',
  };
  let t = s.group === 'attack' ? `Causa dano${el ? ' de ' + el : ''} ${area[s.area.type] ?? ''}.` : s.group === 'healing' ? 'Cura vida.' : '';
  if (s.group === 'attack') t += s.area.type === 'single' || s.area.type === 'chain' ? ' Boa contra um monstro forte.' : ' Boa quando há vários monstros juntos.';
  if (s.scale === 'distance' || s.scale === 'weapon') t += ' O dano sobe com o seu skill de arma e o level.';
  else if (s.scale === 'magic') t += ' O dano sobe com o magic level e o level.';
  t += ` Espera ${secs(s.cooldown || 2000)} para usar de novo.`;
  if (s.monk) t += s.monk === 'builder' ? ' Builder: gera 1 de Harmony.' : ' Spender: gasta a Harmony e bate mais forte.';
  return t;
}

/** campo numérico com botões − e + (no PC e no celular), para ajustar a porcentagem sem digitar */
function Num(props: { value: number; min: number; max: number; step?: number; onChange: (v: number) => void }) {
  const st = props.step ?? 1;
  const set = (v: number) => props.onChange(Math.max(props.min, Math.min(props.max, Math.round(v))));
  return (
    <span class="num-step">
      <button type="button" aria-label="Diminuir" disabled={props.value <= props.min} onClick={() => set(props.value - st)}>−</button>
      <input class="count-num" type="number" min={props.min} max={props.max} value={props.value} onInput={(e) => set(Number((e.target as HTMLInputElement).value))} />
      <button type="button" aria-label="Aumentar" disabled={props.value >= props.max} onClick={() => set(props.value + st)}>+</button>
    </span>
  );
}

const CHALLENGE_TEXT: Record<string, string> = {
  'exeta res': 'Challenge: os monstros em volta (3x3) passam a atacar você. A caça automática usa quando algum deles está em outro jogador ou ainda sem alvo.',
  'exeta amp res': 'Chivalrous Challenge: até 5 monstros a até 7 quadrados passam a atacar você e os que estão longe são puxados para o seu lado.',
  'exana amp res': 'Divine Dazzle: até 3 monstros a até 7 quadrados vêm até você e ficam 4 segundos sem ataques de longe.',
  'exori mas res': 'Balanced Brawl: os monstros num leque à sua frente (até 6 quadrados) passam a atacar você.',
};

export function AutoHelper(props: {
  settings: HelperSettings; vocation: VocationId; level: number; ml: number; promotion: number; vip: boolean; items: ItemInstance[];
  /** nomes dos outros membros da party (para escolher quem o Exura Sio cura) */
  party?: string[];
  onSave: (s: HelperSettings) => void; onSpells: () => void;
  /** cada mudança já vale na hora (a barra de baixo é a mesma lista: as duas mudam juntas) */
  onLive?: (s: HelperSettings) => void;
}) {
  const [s, setS0] = useState<HelperSettings>(() => JSON.parse(JSON.stringify(props.settings)));
  const setS = (n: HelperSettings) => { setS0(n); props.onLive?.(n); };
  // mudou na barra de baixo com a janela aberta: espelha aqui
  useEffect(() => { setS0((cur) => (JSON.stringify(cur.hotbar) === JSON.stringify(props.settings.hotbar) ? cur : { ...cur, hotbar: JSON.parse(JSON.stringify(props.settings.hotbar)) })); }, [props.settings.hotbar]);
  const [tab, setTab] = useState<Tab>('geral');
  const [find, setFind] = useState('');
  const [msg, setMsg] = useState('');
  const [lootName, setLootName] = useState('');
  const [keepName, setKeepName] = useState('');
  // passo a passo: abre sozinho quando nada da barra está ligado para a caça (jogador ainda não configurou)
  const [help, setHelp] = useState(() => !props.settings.hotbar.some((x) => x?.auto));
  const cat = catalogNow();
  const names = useMemo(() => (cat ? [...cat.values()].map((t) => t.name) : []), [cat]);
  const voc = props.vocation, lvl = props.level;
  const can = (sp: SpellDef) => !spellBlock(sp, voc, lvl, props.ml, props.promotion);

  const setSlot = (i: number, v: Partial<HotbarSlot>) => setS({ ...s, hotbar: s.hotbar.map((x, k) => (k === i && x ? { ...x, ...v } : x)) });
  const remove = (i: number) => setS({ ...s, hotbar: s.hotbar.map((x, k) => (k === i ? null : x)) });
  // prioridade: troca com o espaço anterior/seguinte do mesmo tipo (a ordem da barra é a ordem de uso)
  const move = (i: number, dir: -1 | 1, kind: Kind) => {
    const idx = s.hotbar.map((x, k) => (x && kindOf(x) === kind ? k : -1)).filter((k) => k >= 0);
    const at = idx.indexOf(i), j = idx[at + dir];
    if (j === undefined) return;
    const bar = [...s.hotbar]; [bar[i], bar[j]] = [bar[j]!, bar[i]!];
    setS({ ...s, hotbar: bar });
  };
  const add = (use: HotbarSlot['use'], o: Partial<HotbarSlot>) => {
    const i = s.hotbar.findIndex((x) => !x);
    if (i < 0) { setMsg(`A barra de ações está cheia (${s.hotbar.length} espaços). Tire algo antes de adicionar.`); return; }
    const bar = [...s.hotbar]; bar[i] = mkSlot(use, o);
    setS({ ...s, hotbar: bar }); setMsg('');
  };

  // o que dá para adicionar em cada aba: tudo da vocation, com o que faz e o que precisa (o que ainda não libera aparece apagado)
  const all = spellsNow();
  const healAmount = (h: (typeof HEAL_SPELLS)[number]) => {
    const [a1, b1, a2, b2] = h.ml;
    return `${Math.round(lvl * 0.2 + props.ml * a1 + b1)} a ${Math.round(lvl * 0.2 + props.ml * a2 + b2)}`;
  };
  const spellOpt = (x: SpellDef): Opt => {
    const lock = x.kind === 'rune' ? (props.ml < x.ml ? `precisa de magic level ${x.ml} (o seu é ${props.ml})` : null) : spellBlock(x, voc, lvl, props.ml, props.promotion);
    return {
      key: x.kind === 'rune' ? 'i:' + x.item : 's:' + x.words, name: x.name, words: x.kind === 'rune' ? undefined : x.words,
      spell: x.kind === 'rune' ? undefined : x, item: x.kind === 'rune' ? x.item ?? undefined : undefined, sort: x.kind === 'rune' ? 10000 + x.ml : x.level,
      req: x.kind === 'rune' ? `Runa · magic level ${x.ml} · ${(x.price ?? 0).toLocaleString('pt-BR')} gp do banco por uso` : `Level ${x.level}${x.promotion ? ' · só promovidos' : ''} · ${x.mana} de mana${x.soul ? ` · ${x.soul} de soul` : ''}`,
      what: whatSpell(x), lock: lock ? 'Bloqueada: ' + lock.replace(/^level (\d+)$/, 'libera no level $1') : null,
    };
  };
  const healOpts: Opt[] = [
    ...HEAL_SPELLS.filter((h) => h.vocations.includes(voc)).map((h): Opt => ({
      key: 's:' + h.words, name: h.name, words: h.words, spell: spellByWords(h.words), sort: h.level,
      req: `Level ${h.level} · ${h.cost} de mana`,
      what: h.target === 'friend' ? `Cura um membro da party a até 7 quadrados (escolha quem, ou o mais ferido). Hoje curaria cerca de ${healAmount(h)} de vida. Não cura você.`
        : h.target === 'party' ? `Cura você e os membros da party por perto. Hoje curaria cerca de ${healAmount(h)} de vida em cada um.`
        : `Cura a sua vida. Hoje curaria cerca de ${healAmount(h)} de vida (sobe com level e magic level).`,
      lock: lvl < h.level ? `Bloqueada: libera no level ${h.level}` : null,
    })),
    ...Object.entries(POTIONS).filter(([, p]) => !p.vocs?.length || p.vocs.includes(voc)).map(([n, p]): Opt | null => {
      const t = cat && [...cat.values()].find((x) => x.name === n);
      if (!t) return null;
      const block = potionBlock(p, voc, lvl);
      return {
        key: 'i:' + t.id, name: n, item: t.id, sort: p.level ?? 0,
        req: `Poção · ${p.level ? `level ${p.level}` : 'qualquer level'}${p.vocs?.length ? ' · ' + p.vocs.join(', ') : ' · todas as vocations'}`,
        what: `Recupera ${p.min} a ${p.max} de ${p.kind === 'mana' ? 'mana' : p.kind === 'spirit' ? 'vida' : 'vida'}${p.mmin ? ` e ${p.mmin} a ${p.mmax} de mana` : ''}. Custa ${(potionPriceByName(n) ?? 0).toLocaleString('pt-BR')} gp do banco a cada uso (sem gold, não usa).`,
        lock: block ? 'Bloqueada: ' + block : null,
      };
    }).filter((x): x is Opt => !!x),
    ...all.filter((r) => r.kind === 'rune' && r.group === 'healing' && r.usable && r.item).map(spellOpt),
  ];
  const atkOpts: Opt[] = all.filter((x) => (x.group === 'attack' || ((x.group === 'field' || x.group === 'support') && x.kind === 'rune')) && x.usable && (x.kind === 'rune' || x.vocations.includes(voc))).map(spellOpt);
  const buffOpts: Opt[] = Object.values(BUFFS).map((b) => [b, spellByWords(b.words)] as const)
    .filter(([, sp]) => sp && sp.vocations.includes(voc)).map(([b, sp]) => ({ ...spellOpt(sp!), name: b.name, what: b.desc })).concat(
      // desafios (puxam o agro dos monstros para você), como no Global
      CHALLENGE_SPELLS.map((w) => spellByWords(w)).filter((sp): sp is NonNullable<typeof sp> => !!sp && sp.vocations.includes(voc)).map((sp) => ({ ...spellOpt(sp), what: CHALLENGE_TEXT[sp.words] ?? 'Puxa o agro dos monstros para você.' })));
  const addPick = (kind: Kind, pick: string) => {
    const [t, v] = [pick.slice(0, 1), pick.slice(2)];
    const use = t === 's' ? { spell: v } : { item: Number(v) };
    if (kind === 'heal') {
      const name = t === 'i' ? cat?.get(Number(v))?.name ?? '' : '';
      const mana = potionOf(name)?.kind === 'mana';
      add(use, { stat: mana ? 'mana' : 'hp', below: mana ? 40 : 60 });
    } else if (kind === 'attack') {
      const sp = t === 's' ? spellByWords(v) : runeByItem(Number(v));
      add(use, { minMana: t === 's' ? 30 : 0, minMonsters: sp && sp.area.type !== 'single' && sp.area.type !== 'chain' ? 2 : 1, ...(sp?.monk === 'spender' ? { minHarmony: 5 } : {}) });
    } else add(use, { minMana: 10 });
  };

  const free = s.hotbar.filter((x) => !x).length;
  const rows = (kind: Kind) => {
    const list = s.hotbar.map((sl, i) => [sl, i] as const).filter(([sl]) => sl && kindOf(sl) === kind) as Array<readonly [HotbarSlot, number]>;
    if (!list.length) return <div class="ah2-empty"><b>Nada configurado ainda.</b><span>Escolha abaixo e clique em Adicionar: vai para a barra de ações e já vale na caça.</span></div>;
    return (
      <ul class="ah2-slots">
        {list.map(([sl, i], n) => {
          const sp = 'spell' in sl.use ? spellByWords(sl.use.spell) : runeByItem(sl.use.item);
          const buff = 'spell' in sl.use ? BUFFS[sl.use.spell] : undefined;
          const heal = 'spell' in sl.use ? HEAL_SPELLS.find((h) => h.words === (sl.use as { spell: string }).spell) : undefined;
          const itemName = 'item' in sl.use ? cat?.get(sl.use.item)?.name ?? '' : '';
          const potion = 'item' in sl.use ? potionOf(itemName) : undefined;
          // custo por uso (gold do banco): poções pelo preço da loja, runas pelo preço da runa
          const cost = potion ? potionPriceByName(itemName) ?? 0 : 'item' in sl.use && sp?.price ? sp.price : 0;
          const isSpell = 'spell' in sl.use;
          const manaCost = isSpell ? heal?.cost ?? sp?.mana : undefined;
          const statName = sl.stat === 'mana' ? 'mana' : 'vida';
          const friend = heal?.target === 'friend';
          const rule = kind === 'heal' && friend ? `curar ${sl.who || 'o membro da party mais ferido'} quando a vida dele < ${sl.below}%`
            : kind === 'heal' && heal?.target === 'party' ? `usar quando a vida de alguém (você ou a party) < ${sl.below}%`
            : kind === 'heal' ? `usar quando ${statName} < ${sl.below}%`
            : kind === 'attack' ? `usar com ${sl.minMonsters}+ monstro${sl.minMonsters === 1 ? '' : 's'} na área${sp?.monk === 'spender' ? ` e Harmony ${sl.minHarmony ?? 5}+` : ''}`
            : kind === 'buff' ? 'reaplicar quando o efeito acabar' : 'uso manual';
          const manaRule = isSpell && kind !== 'other' && sl.minMana > 0 ? ` · mana > ${sl.minMana}%` : '';
          return (
            <li key={i} class={'ah2-slot ah2-k-' + kind + (sl.auto ? '' : ' off')}>
              <span class="ah2-prio" title="Prioridade (ordem na barra)">{n + 1}º</span>
              <span class="ah2-slot-ico">{sp && isSpell ? <SpellIcon spell={sp} size={32} dim={!sl.auto} /> : 'item' in sl.use ? <span class="item-slot"><ItemImg typeId={sl.use.item} count={1} /></span> : null}</span>
              <div class="ah2-slot-main">
                <div class="ah2-slot-top">
                  <b class="ah2-slot-name">{slotLabel(sl)}</b>
                  <span class="ah2-tag" title="Posição na barra de ações">barra {i + 1}</span>
                  {cost > 0 && <span class="ah2-tag ah2-gold" title="Gold do banco a cada uso (sem gold, não usa)">💰 {gp(cost)} gp/uso</span>}
                  {manaCost !== undefined && <span class="ah2-tag ah2-mana" title="Mana gasta a cada uso">{manaCost} mana</span>}
                </div>
                <div class="ah2-rule" title="Quando a caça automática usa">
                  <span class="ah2-caret">▸</span>{sl.auto ? rule + manaRule : 'desligado: a caça automática não usa'}
                </div>
                <div class="ah2-cond">
                  {kind === 'heal' && friend && <>
                    <span>Curar</span>
                    <select value={sl.who ?? ''} onChange={(e) => { const v = (e.target as HTMLSelectElement).value; setSlot(i, { who: v || undefined, stat: 'hp' }); }}>
                      <option value="">o membro mais ferido</option>
                      {[...new Set([...(props.party ?? []), ...(sl.who ? [sl.who] : [])])].map((n) => <option key={n} value={n}>{n}{props.party?.includes(n) ? '' : ' (fora da party)'}</option>)}
                    </select>
                    <span>quando a vida dele estiver abaixo de</span>
                    <Num min={1} max={100} step={5} value={sl.below} onChange={(v) => setSlot(i, { below: v })} /><span>%</span>
                    <span class="ah2-thr hp" title={`Dispara abaixo de ${sl.below}% de vida`}><i style={{ width: `${Math.max(0, Math.min(100, sl.below))}%` }} /></span>
                    {!props.party?.length && <span class="ah2-note">Entre numa party para o Exura Sio ter em quem curar.</span>}
                  </>}
                  {kind === 'heal' && !friend && <>
                    <span>Usar quando a</span>
                    <select value={sl.stat} onChange={(e) => setSlot(i, { stat: (e.target as HTMLSelectElement).value as 'hp' | 'mana' })}>
                      <option value="hp">vida</option><option value="mana">mana</option>
                    </select>
                    <span>estiver abaixo de</span>
                    <Num min={1} max={100} step={5} value={sl.below} onChange={(v) => setSlot(i, { below: v })} /><span>%</span>
                    <span class={'ah2-thr ' + (sl.stat === 'mana' ? 'mana' : 'hp')} title={`Dispara abaixo de ${sl.below}% de ${statName}`}><i style={{ width: `${Math.max(0, Math.min(100, sl.below))}%` }} /></span>
                  </>}
                  {kind === 'attack' && <>
                    <span>Usar com pelo menos</span>
                    <Num min={1} max={10} step={1} value={sl.minMonsters} onChange={(v) => setSlot(i, { minMonsters: v })} />
                    <span>monstro(s) na área</span>
                    {sp?.monk === 'spender' && <>
                      <span>e Harmony</span>
                      <Num min={0} max={5} step={1} value={sl.minHarmony ?? 5} onChange={(v) => setSlot(i, { minHarmony: v })} /><span>ou mais</span>
                    </>}
                  </>}
                  {kind === 'buff' && <span>Reaplica sozinho quando acaba</span>}
                  {isSpell && kind !== 'other' && <>
                    <span>· mana acima de</span>
                    <Num min={0} max={99} step={5} value={sl.minMana} onChange={(v) => setSlot(i, { minMana: v })} /><span>%</span>
                  </>}
                </div>
                <div class="ah2-info">
                  {buff ? buff.desc : heal ? `${heal.target === 'friend' ? 'Cura um membro da party a até 7 quadrados (não cura você)' : heal.target === 'party' ? 'Cura você e a party por perto' : 'Cura a sua vida'} · ${heal.cost} de mana` : potion ? `Poção: recupera ${potion.min}-${potion.max} de ${potion.kind === 'mana' ? 'mana' : 'vida'}${potion.mmin ? ` e ${potion.mmin}-${potion.mmax} de mana` : ''} · ${gp(potionPriceByName(itemName) ?? 0)} gp por uso` : spellInfo(sp)}
                </div>
              </div>
              <div class="ah2-slot-side">
                <label class="ah2-sw" title="Ligado: a caça automática usa">
                  <input type="checkbox" checked={sl.auto} onChange={(e) => setSlot(i, { auto: (e.target as HTMLInputElement).checked })} />
                  <i /><span>{sl.auto ? 'ON' : 'OFF'}</span>
                </label>
                <span class="ah2-btns">
                  <button class="tb-button" title="Mais prioridade" disabled={n === 0} onClick={() => move(i, -1, kind)}>▲</button>
                  <button class="tb-button" title="Menos prioridade" disabled={n === list.length - 1} onClick={() => move(i, 1, kind)}>▼</button>
                  <button class="tb-button" title="Tirar da barra" onClick={() => remove(i)}>✕</button>
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };
  const adder = (kind: Kind, opts: Opt[]) => {
    const list = [...opts].sort((x, y) => (x.lock ? 1 : 0) - (y.lock ? 1 : 0) || x.sort - y.sort || x.name.localeCompare(y.name))
      .filter((o) => !find || (o.name + ' ' + (o.words ?? '')).toLowerCase().includes(find.toLowerCase()));
    const inBar = new Set(s.hotbar.filter(Boolean).map((x) => ('spell' in x!.use ? 's:' + x!.use.spell : 'i:' + x!.use.item)));
    return (
      <div class="ah2-picker">
        <div class="ah2-picker-head">
          <b>＋ Adicionar à barra</b>
          <span class="ah2-tag">{free} espaço{free === 1 ? '' : 's'} livre{free === 1 ? '' : 's'}</span>
          <input placeholder="Procurar por nome ou palavras..." value={find} onInput={(e) => setFind((e.target as HTMLInputElement).value)} />
          {msg && <span class="error small ah2-msg">{msg}</span>}
        </div>
        <ul class="ah2-cards">
          {list.map((o) => (
            <li key={o.key} class={'ah2-card' + (o.lock ? ' locked' : '') + (inBar.has(o.key) ? ' inbar' : '')}>
              <span class="ah2-card-ico">{o.spell ? <SpellIcon spell={o.spell} size={32} dim={!!o.lock} /> : o.item ? <span class="item-slot"><ItemImg typeId={o.item} count={1} /></span> : null}</span>
              <div class="ah2-card-text">
                <div><b>{o.name}</b>{o.words && <span class="ah2-words">"{o.words}"</span>}</div>
                <div class="ah2-what">{o.what}</div>
                <div class="ah2-req">{o.req}</div>
                {o.lock && <div class="ah2-lock">🔒 {o.lock}</div>}
              </div>
              <button class="tb-button green" disabled={!!o.lock || inBar.has(o.key)} onClick={() => addPick(kind, o.key)}>{inBar.has(o.key) ? '✔ Na barra' : 'Adicionar'}</button>
            </li>
          ))}
          {!list.length && <li class="ah2-none">Nada encontrado.</li>}
        </ul>
      </div>
    );
  };
  const addLoot = () => {
    const t = cat && [...cat.values()].find((x) => x.name.toLowerCase() === lootName.trim().toLowerCase());
    if (t && !s.skipLoot.includes(t.id)) setS({ ...s, skipLoot: [...s.skipLoot, t.id] });
    setLootName('');
  };
  const addKeep = () => {
    const t = cat && [...cat.values()].find((x) => x.name.toLowerCase() === keepName.trim().toLowerCase());
    if (t && !s.keepItems.includes(t.id)) setS({ ...s, keepItems: [...s.keepItems, t.id] });
    setKeepName('');
  };

  // resumo para o cabeçalho e as abas (só o que já está na configuração)
  const used = s.hotbar.filter(Boolean) as HotbarSlot[];
  const countOf = (k: Kind) => used.filter((x) => kindOf(x) === k).length;
  const liveOf = (k: Kind) => used.filter((x) => x.auto && kindOf(x) === k).length;
  const curesOn = CONDITION_CURES.filter((c) => s.cures?.[c.id] !== false && c.spells.some((w) => spellByWords(w)?.vocations.includes(voc))).length;
  const eliteOn = (['super', 'mega', 'ultra'] as const).filter((k) => s.elite?.[k]).length;
  const tabBadge: Record<Tab, string> = {
    geral: '', heal: String(countOf('heal')), attack: String(countOf('attack')), buff: String(countOf('buff')),
    cond: String(curesOn), elite: eliteOn ? String(eliteOn) : '', loot: s.skipLoot.length + s.keepItems.length ? String(s.skipLoot.length + s.keepItems.length) : '',
  };
  const antiRed = s.antiRed !== false;
  const mvMode = (s.movement ?? DEFAULT_MOVEMENT).mode;
  const tabInfo = TABS.find((t) => t[0] === tab)!;
  const opt = (checked: boolean, on: (v: boolean) => void, title: string, desc: ComponentChildren, icon: string) => (
    <label class={'ah2-opt' + (checked ? ' on' : '')}>
      <span class="ah2-opt-ico">{icon}</span>
      <span class="ah2-opt-txt"><b>{title}</b><small>{desc}</small></span>
      <span class="ah2-sw"><input type="checkbox" checked={checked} onChange={(e) => on((e.target as HTMLInputElement).checked)} /><i /><span>{checked ? 'ON' : 'OFF'}</span></span>
    </label>
  );

  return (
    <div class="ah2">
      <header class="ah2-head">
        <div class="ah2-emblem" title="Itens ligados para a caça / espaços usados na barra">
          <span class="ah2-radar" />
          <b>{used.filter((x) => x.auto).length}<small>/{used.length}</small></b>
        </div>
        <div class="ah2-head-txt">
          <div class="ah2-title">Auto Helper <span class="ah2-title-sub">central de comando</span>
            <button class="ah2-help" onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="ah2-sub">Curas, ataques e suporte da sua barra de ações, usados sozinhos na caça, na ordem da barra.</div>
          <div class="ah2-chips">
            <span class="ah2-chip ok" title="As mudanças valem na hora"><i class="ah2-dot" />Salvo automaticamente</span>
            <span class="ah2-chip" title="Espaços ocupados na barra de ações">Barra {used.length}/{s.hotbar.length}</span>
            <span class="ah2-chip hp" title="Curas ligadas">❤ {liveOf('heal')}</span>
            <span class="ah2-chip atk" title="Ataques ligados">⚔ {liveOf('attack')}</span>
            <span class="ah2-chip buf" title="Suportes ligados">✨ {liveOf('buff')}</span>
            <span class={'ah2-chip ' + (antiRed ? 'red' : 'dim')} title="Anti-Red: sem magia em área com jogador na tela">Anti-Red {antiRed ? 'ON' : 'OFF'}</span>
            <span class="ah2-chip" title="Movimentação do jogador">🧭 {MV_NAME[mvMode] ?? mvMode}</span>
          </div>
        </div>
        <button class="tb-button green ah2-close" onClick={() => props.onSave(s)}>Fechar</button>
      </header>

      {help && (
        <div class="ah2-steps">
          <div><i>❤️</i><b>1. Curas primeiro</b><small>Na aba Curas, adicione uma magia ou poção de vida e escolha a porcentagem (ex.: vida abaixo de 60%).</small></div>
          <div><i>⚔️</i><b>2. Ataques</b><small>Na aba Ataque, adicione magias e runas. Elas vão para a sua barra de magias: a ordem da barra é a prioridade.</small></div>
          <div><i>⚙️</i><b>3. Ajuste</b><small>Em Geral: comida, venda do loot, Anti-Red, reação ao PvP e o jeito de andar (natural, lurar ou distância).</small></div>
          <div><i>▶️</i><b>4. Caçar</b><small>Feche e aperte Caçar ao lado da barra. Tudo é salvo sozinho a cada mudança.</small></div>
        </div>
      )}

      <div class="ah2-wrap">
        <nav class="ah2-tabs">
          {TABS.map(([id, label, icon, hint]) => (
            <button key={id} class={'ah2-tab' + (tab === id ? ' on' : '')} title={hint} onClick={() => { setTab(id); setFind(''); setMsg(''); }}>
              <span class="ah2-tab-ico">{icon}</span><span class="ah2-tab-lb">{label}</span>{tabBadge[id] && <em>{tabBadge[id]}</em>}
            </button>
          ))}
        </nav>
        <section class="ah2-body">
          <div class="ah2-sec-head"><span>{tabInfo[2]}</span><b>{tabInfo[1]}</b><small>{tabInfo[3]}</small></div>
          {tab === 'geral' && <>
            <div class="ah2-note">
              O Auto Helper caça por você: anda até os monstros das boxes marcadas no menu de Hunts, ataca com a arma e usa sozinho o que estiver nas abas
              <b> Curas</b>, <b>Ataque</b> e <b>Suporte</b>. Tudo isso fica na sua barra de ações, e a ordem é a prioridade: ele usa a primeira opção liberada que cumprir a condição.
              Ligue e desligue a caça pelo botão <b>Caçar</b> ao lado da barra. As mudanças valem na hora e são salvas sozinhas.
              <small>Por segurança ele usa só uma cura, uma poção e um ataque por vez, com as mesmas esperas (cooldowns) do Tibia.
                A lista completa de magias da sua vocation fica na <a href="#" onClick={(e) => { e.preventDefault(); props.onSpells(); }}>lista de magias</a>.</small>
            </div>

            <div class="ah2-group">
              <div class="ah2-group-t">🛠️ Rotina</div>
              {opt(s.autoEat, (v) => setS({ ...s, autoEat: v }), 'Comer sozinho', 'Come a comida da mochila (sem comida não há regeneração natural).', '🍖')}
              {opt(!!s.autoSkillPotion, (v) => setS({ ...s, autoSkillPotion: v }), 'Poção de skill automática',
                'Toma a Berserk (knight), Mastermind (sorcerer e druid) ou Bullseye (paladin) sempre que o efeito acabar, pagando o preço da loja com o gold do banco (25 mil cada).', '🧪')}
              {(props.vocation === 'sorcerer' || props.vocation === 'druid') &&
                opt(!!s.autoMagicShield, (v) => setS({ ...s, autoMagicShield: v }), 'Utamo Vita automático',
                  'Mantém o Magic Shield sempre ligado fora da zona protegida, renovando antes de acabar (quando faltam 20 s ou o escudo cai abaixo de 70%), para nunca ficar sem ele. Precisa do level 14 e de 50 de mana.', '🔵')}
            </div>

            <div class="ah2-group">
              <div class="ah2-group-t">💰 Venda do loot</div>
              {opt(s.sellWhenFull, (v) => setS({ ...s, sellWhenFull: v }), 'Mochila cheia: vender',
                `Vende o loot comum sozinho quando a venda na hunt estiver liberada (${props.vip ? 'VIP: a cada 10 min' : 'a cada 20 min; VIP a cada 10 min'}).`, '🎒')}
              {opt(s.autoSell !== false, (v) => setS({ ...s, autoSell: v }), 'Venda automática por tempo',
                'Vende o loot comum na hunt sempre que a venda estiver liberada (os itens a guardar são escolhidos ao entrar na hunt; raros, épicos e lendários nunca são vendidos).', '⏱️')}
            </div>

            <div class="ah2-group">
              <div class="ah2-group-t">🛡️ Outros jogadores</div>
              {opt(s.respectful, (v) => setS({ ...s, respectful: v }), 'Respectful hunt', 'Não ataca monstros que outro jogador já está atacando.', '🤝')}
              <button type="button" class={'anti-red' + (antiRed ? ' on' : '')} onClick={() => setS({ ...s, antiRed: s.antiRed === false })}
                title="Ligado: o personagem nunca usa magia ou runa em área quando há outro jogador atacável na tela, para não pegar skull sem querer. Se o revide estiver ligado e alguém atacar você, as magias em área voltam a ser usadas.">
                Anti-Red {antiRed ? 'ON' : 'OFF'}
              </button>
              <p class="ah2-hint">Anti-Red ligado: nada de magia ou runa em área com outro jogador na tela (para não pegar skull). Com o revide ligado, se alguém te atacar, as magias em área voltam a ser usadas.</p>
              <label class="ah2-pvp"><span>⚔️ PvP — quando outro jogador me atacar:</span>
                <select value={s.pvpReact ?? 'fight'} onChange={(e) => setS({ ...s, pvpReact: (e.target as HTMLSelectElement).value as any })}>
                  <option value="fight">revidar (atacar de volta)</option>
                  <option value="flee">fugir para o templo (se puder)</option>
                  <option value="ignore">ignorar e continuar caçando</option>
                </select></label>
            </div>

            <div class="ah2-group">
              <div class="ah2-group-t">🧭 Movimentação do jogador</div>
              {(() => {
                const mv = s.movement ?? DEFAULT_MOVEMENT;
                const setMv = (o: Partial<typeof mv>) => setS({ ...s, movement: { ...mv, ...o } });
                const num = (v: number, lo: number, hi: number, k: 'distance' | 'lureCount' | 'lureStopHp') => (
                  <Num min={lo} max={hi} step={1} value={v} onChange={(v) => setMv({ [k]: v } as Partial<typeof mv>)} />);
                return <div class="ah2-mv">
                  <label class={'ah2-mv-opt' + (mv.mode === 'natural' ? ' on' : '')}><input type="radio" name="ah-mv" checked={mv.mode === 'natural'} onChange={() => setMv({ mode: 'natural' })} />
                    <i>🚶</i><span><b>Natural</b>Anda até o monstro e ataca, um de cada vez (como é hoje).</span></label>
                  <label class={'ah2-mv-opt' + (mv.mode === 'lure' ? ' on' : '')}><input type="radio" name="ah-mv" checked={mv.mode === 'lure'} onChange={() => setMv({ mode: 'lure' })} />
                    <i>🌀</i><span><b>Lurar (fazer box)</b>Junta vários monstros atrás de você antes de lutar, para matar todos juntos com magias de área. Mais XP, mais risco.</span></label>
                  <label class={'ah2-mv-opt' + (mv.mode === 'distance' ? ' on' : '')}><input type="radio" name="ah-mv" checked={mv.mode === 'distance'} onChange={() => setMv({ mode: 'distance' })} />
                    <i>🏹</i><span><b>Manter distância</b>Fica longe do alvo e recua quando os monstros chegam perto, sem sair do alcance do ataque. Para paladins, sorcerers e druids.</span></label>
                  {mv.mode === 'lure' && <div class="ah2-cond ah2-mv-sub">
                    <span>Juntar</span>{num(mv.lureCount, 2, 30, 'lureCount')}<span>monstros · parar de juntar se a vida cair abaixo de</span>{num(mv.lureStopHp, 10, 95, 'lureStopHp')}<span>%</span>
                  </div>}
                  {mv.mode === 'distance' && <div class="ah2-cond ah2-mv-sub">
                    <span>Ficar a pelo menos</span>{num(mv.distance, 2, 6, 'distance')}<span>quadrados do alvo</span>
                    {(voc === 'knight' || voc === 'monk') && <span class="error small">Sua vocation ataca de perto: este modo só funciona com arma ou magia de distância.</span>}
                  </div>}
                </div>;
              })()}
            </div>
          </>}
          {tab === 'heal' && <>
            <p class="ah2-hint">Magias de cura, poções e runas de cura. Escolha se usam pela vida ou pela mana e a porcentagem. A primeira da lista tem prioridade.</p>
            {rows('heal')}
            {adder('heal', healOpts)}
          </>}
          {tab === 'attack' && <>
            <p class="ah2-hint">Magias e runas de ataque — é a mesma lista da sua barra de magias (mudou aqui, muda lá). Para magias de área, escolha quantos monstros precisam ser atingidos para valer a pena. A primeira liberada é usada.
              {voc === 'monk' ? ' Monk: os builders geram Harmony e os spenders só são usados com a Harmony escolhida (5 = dano máximo).' : ''}</p>
            {rows('attack')}
            {adder('attack', atkOpts)}
          </>}
          {tab === 'buff' && <>
            <p class="ah2-hint">Magias de suporte (haste, magic shield, posturas, virtudes...). O Auto Helper reaplica cada uma quando o efeito acaba.</p>
            {rows('buff')}
            {adder('buff', buffOpts)}
          </>}
          {tab === 'cond' && <>
            <p class="ah2-hint">Quando o personagem pega uma condição, o Auto Helper usa a magia que cura (se a sua vocation tiver e houver mana). Desligue para deixar a condição passar sozinha.</p>
            <ul class="ah2-conds">
              {CONDITION_CURES.map((c) => {
                const avail = c.spells.map((w) => spellByWords(w) ?? (BUFFS[w] ? spellByWords(w) : undefined)).filter((x): x is SpellDef => !!x && x.vocations.includes(voc));
                const ready = avail.find((x) => lvl >= x.level);
                const on = !!avail.length && s.cures?.[c.id] !== false;
                const st = !avail.length ? 'lock' : !ready ? 'wait' : on ? 'on' : 'off';
                return (
                  <li key={c.id} class={'ah2-condc ' + st}>
                    <span class="ah2-condc-ico">{ready ? <SpellIcon spell={ready} size={32} dim={!on} /> : avail[0] ? <SpellIcon spell={avail[0]} size={32} dim /> : <span class="ah2-noico">—</span>}</span>
                    <div class="ah2-condc-txt">
                      <b>{c.name}</b>
                      <small>{c.desc}</small>
                      <span class={'ah2-state ' + st}>{ready ? `${on ? '✔ cura' : 'desligada: curaria'} com ${ready.name} (${ready.words}, ${ready.mana} de mana)` : avail.length ? `🔒 ${avail[0]!.name} no level ${avail[0]!.level}` : c.spells.length ? '🔒 sua vocation não tem magia para isso' : 'não tem cura'}</span>
                    </div>
                    <label class="ah2-sw" title={avail.length ? 'Ligado: cura sozinho' : 'Sem magia disponível'}>
                      <input type="checkbox" disabled={!avail.length} checked={on} onChange={(e) => setS({ ...s, cures: { ...(s.cures ?? {}), [c.id]: (e.target as HTMLInputElement).checked } })} />
                      <i /><span>{on ? 'ON' : 'OFF'}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </>}
          {tab === 'elite' && <>
            <p class="ah2-hint">Super, Mega e Ultra nascem em qualquer respawn. Eles vão para cima como os outros monstros e somem após 2 minutos sem levar ataque.
              Desligado: seu personagem não procura esses monstros e não os acerta de propósito, mas se defende quando um deles o ataca.</p>
            <div class="ah2-elites">
              {([
                ['super', 'Super', 'bronze', ['5x vida', '7x XP']],
                ['mega', 'Mega', 'prata', ['10x vida', '15x XP', '3x gold', '2x drops']],
                ['ultra', 'Ultra', 'dourado', ['25x vida', '30x XP', '8x gold', '3x drops']],
              ] as const).map(([k, label, color, stats]) => {
                const on = s.elite?.[k] ?? false;
                return (
                  <label key={k} class={'ah2-elite ah2-elite-' + k + (on ? ' on' : '')}>
                    <span class="ah2-elite-crest">★</span>
                    <b class={'elite-' + k}>{label}</b>
                    <small>contorno {color}</small>
                    <span class="ah2-elite-stats">{stats.map((t) => <em key={t}>{t}</em>)}</span>
                    <span class="ah2-sw"><input type="checkbox" checked={on}
                      onChange={(e) => setS({ ...s, elite: { ...(s.elite ?? { super: false, mega: false, ultra: false }), [k]: (e.target as HTMLInputElement).checked } })} />
                      <i /><span>{on ? 'Atacar' : 'Evitar'}</span></span>
                  </label>
                );
              })}
            </div>
          </>}
          {tab === 'loot' && <>
            <datalist id="ah-items">{names.slice(0, 7000).map((n) => <option key={n} value={n} />)}</datalist>
            <div class="ah2-loot">
              <div class="ah2-lootbox skip">
                <div class="ah2-group-t">🚫 Não pegar <em>{s.skipLoot.length}</em></div>
                <p class="ah2-hint">Itens desta lista não são pegos. O gold vai sempre direto para o banco.</p>
                <div class="ah2-loot-add">
                  <input list="ah-items" value={lootName} placeholder="Nome do item" onInput={(e) => setLootName((e.target as HTMLInputElement).value)} onKeyDown={(e) => e.key === 'Enter' && addLoot()} />
                  <button class="tb-button" onClick={addLoot}>Não pegar</button>
                </div>
                <ul class="ah2-chipsl">
                  {s.skipLoot.map((id) => (
                    <li key={id}><span class="item-slot small"><ItemImg typeId={id} count={1} /></span><span>{cat?.get(id)?.name ?? id}</span>
                      <button class="tb-button" title="Voltar a pegar" onClick={() => setS({ ...s, skipLoot: s.skipLoot.filter((x) => x !== id) })}>✕</button></li>
                  ))}
                  {!s.skipLoot.length && <li class="ah2-none">Pegando tudo.</li>}
                </ul>
              </div>
              <div class="ah2-lootbox keep">
                <div class="ah2-group-t">🔐 Não vender <em>{s.keepItems.length}</em></div>
                <p class="ah2-hint">A venda automática e o botão "Vender loot" da loja nunca vendem estes itens. Também dá para marcar com o botão direito num item do inventário.</p>
                <div class="ah2-loot-add">
                  <input list="ah-items" value={keepName} placeholder="Nome do item" onInput={(e) => setKeepName((e.target as HTMLInputElement).value)} onKeyDown={(e) => e.key === 'Enter' && addKeep()} />
                  <button class="tb-button" onClick={addKeep}>Não vender</button>
                </div>
                <ul class="ah2-chipsl">
                  {s.keepItems.map((id) => (
                    <li key={id}><span class="item-slot small"><ItemImg typeId={id} count={1} /></span><span>{cat?.get(id)?.name ?? id}</span>
                      <button class="tb-button" title="Permitir vender" onClick={() => setS({ ...s, keepItems: s.keepItems.filter((x) => x !== id) })}>✕</button></li>
                  ))}
                  {!s.keepItems.length && <li class="ah2-none">Vendendo todo o loot comum.</li>}
                </ul>
              </div>
            </div>
          </>}
        </section>
      </div>
    </div>
  );
}
